import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import {
  MapPin,
  Clock,
  Briefcase,
  Wallet,
  ArrowLeft,
  CheckCircle2,
  ChevronRight,
  Mail,
  Phone,
  Send,
  Users,
} from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { VACANCIES, getVacancyBySlug, type Vacancy } from "@/data/vacancies";
import { JobApplicationForm } from "@/components/site/JobApplicationForm";

const SITE_URL = "https://elvion-group.nl";

export const Route = createFileRoute("/werken-bij/$slug")({
  // Alleen de slug teruggeven: de vacature bevat een icoon-component dat niet serialiseerbaar is.
  loader: ({ params }) => {
    if (!getVacancyBySlug(params.slug)) throw notFound();
    return { slug: params.slug };
  },
  head: ({ loaderData }) => {
    const vacancy = loaderData ? getVacancyBySlug(loaderData.slug) : undefined;
    if (!vacancy) return {};
    const url = `${SITE_URL}/werken-bij/${vacancy.slug}`;
    const title = `${vacancy.title} (${vacancy.location}) | Vacature Elvion Group Nederland`;
    const description = vacancy.shortDescription;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "nl_NL" },
        { property: "og:site_name", content: "Elvion Group Nederland" },
        { property: "twitter:card", content: "summary_large_image" },
        { property: "twitter:title", content: title },
        { property: "twitter:description", content: description },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "BreadcrumbList",
                itemListElement: [
                  { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
                  { "@type": "ListItem", position: 2, name: "Werken bij ons", item: `${SITE_URL}/werken-bij` },
                  { "@type": "ListItem", position: 3, name: vacancy.title, item: url },
                ],
              },
              {
                "@type": "JobPosting",
                title: vacancy.title,
                description: vacancy.intro,
                employmentType: vacancy.type.toUpperCase().includes("PART") ? "PART_TIME" : "FULL_TIME",
                hiringOrganization: {
                  "@type": "Organization",
                  name: "Elvion Group Nederland",
                  sameAs: SITE_URL,
                },
                jobLocation: {
                  "@type": "Place",
                  address: {
                    "@type": "PostalAddress",
                    addressLocality: vacancy.location,
                    addressCountry: "NL",
                  },
                },
              },
            ],
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <>
      <Header variant="shop" />
      <main className="mx-auto max-w-3xl px-4 py-20 text-center sm:px-6 lg:px-8">
        <h1 className="font-display text-3xl font-extrabold text-foreground">Vacature niet gevonden</h1>
        <p className="mt-3 text-text-secondary">Deze vacature is niet (meer) beschikbaar.</p>
        <Link
          to="/werken-bij"
          className="mt-6 inline-flex items-center gap-2 rounded-md bg-orange px-5 py-3 text-sm font-semibold text-white hover:bg-orange/90"
        >
          <ArrowLeft className="h-4 w-4" />
          Terug naar alle vacatures
        </Link>
      </main>
      <Footer />
    </>
  ),
  errorComponent: ({ error, reset }) => (
    <>
      <Header variant="shop" />
      <main className="mx-auto max-w-3xl px-4 py-20 text-center sm:px-6 lg:px-8">
        <h1 className="font-display text-3xl font-extrabold text-foreground">Er ging iets mis</h1>
        <p className="mt-3 text-text-secondary">{error instanceof Error ? error.message : "Onbekende fout"}</p>
        <button
          onClick={reset}
          className="mt-6 inline-flex items-center gap-2 rounded-md bg-orange px-5 py-3 text-sm font-semibold text-white hover:bg-orange/90"
        >
          Probeer opnieuw
        </button>
      </main>
      <Footer />
    </>
  ),
  component: VacancyDetailPage,
});

function VacancyDetailPage() {
  const { slug } = Route.useLoaderData();
  const vacancy = getVacancyBySlug(slug) as Vacancy;
  const Icon = vacancy.icon;



  const otherVacancies = VACANCIES.filter((v) => v.slug !== vacancy.slug);

  return (
    <>
      <Header variant="shop" />
      <main className="bg-white">
        {/* Breadcrumb + hero */}
        <section className="border-b border-border bg-muted/30">
          <div className="mx-auto max-w-7xl px-4 pt-6 pb-10 sm:px-6 lg:px-8 lg:pb-14">
            <nav className="mb-6 text-xs text-text-secondary">
              <Link to="/" className="hover:text-orange">Home</Link>
              <span className="mx-1.5">/</span>
              <Link to="/werken-bij" className="hover:text-orange">Werken bij ons</Link>
              <span className="mx-1.5">/</span>
              <span className="text-foreground">{vacancy.title}</span>
            </nav>

            <div className="grid items-start gap-8 lg:grid-cols-3">
              <div className="lg:col-span-2">
                <div className="mb-4 flex items-center gap-3">
                  <div className="rounded-full bg-orange/10 p-2.5 text-orange">
                    <Icon className="h-5 w-5" />
                  </div>
                  <p className="text-xs font-medium uppercase tracking-wider text-orange">
                    {vacancy.department}
                  </p>
                </div>
                <h1 className="font-display text-4xl font-extrabold tracking-tight text-foreground sm:text-5xl">
                  {vacancy.title} – vacature bij Elvion Group Nederland ({vacancy.location})
                </h1>
                <p className="mt-4 max-w-2xl text-base text-text-secondary sm:text-lg">
                  {vacancy.intro}
                </p>

                <div className="mt-6 flex flex-wrap gap-2">
                  <Badge icon={MapPin}>{vacancy.location}</Badge>
                  <Badge icon={Clock}>{vacancy.hoursPerWeek}</Badge>
                  <Badge icon={Briefcase}>{vacancy.type}</Badge>
                  <Badge icon={Wallet}>{vacancy.salaryIndication}</Badge>
                </div>
              </div>

              <aside className="rounded-2xl border border-border bg-card p-6 shadow-sm lg:sticky lg:top-24">
                <h2 className="font-display text-lg font-bold text-foreground">Solliciteren</h2>
                <p className="mt-1 text-sm text-text-secondary">
                  Stuur je CV en korte motivatie, wij reageren binnen 2 werkdagen.
                </p>
                <div className="mt-5 space-y-2">
                  <a
                    href="#solliciteer"
                    className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-orange px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-orange/90"
                  >
                    <Send className="h-4 w-4" />
                    Naar sollicitatieformulier
                  </a>
                  <a
                    href="mailto:werkenbij@elvion-group.nl"
                    className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-border bg-card px-4 py-2.5 text-sm font-semibold text-foreground transition-colors hover:border-orange hover:text-orange"
                  >
                    <Mail className="h-4 w-4" />
                    werkenbij@elvion-group.nl
                  </a>
                  <a
                    href="tel:+31850806695"
                    className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-border bg-card px-4 py-2.5 text-sm font-semibold text-foreground transition-colors hover:border-orange hover:text-orange"
                  >
                    <Phone className="h-4 w-4" />
                    085 080 66 95
                  </a>
                </div>
                <p className="mt-4 text-xs text-text-secondary">
                  Vragen over de functie? Neem contact op met onze HR-afdeling.
                </p>
              </aside>
            </div>
          </div>
        </section>

        {/* Detail content */}
        <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
          <div className="grid gap-10 lg:grid-cols-3">
            <div className="space-y-10 lg:col-span-2">
              <Block title="Over de functie">
                <ul className="space-y-2.5">
                  {vacancy.aboutRole.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-text-secondary">
                      <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-orange" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </Block>

              <Block title="Wat ga je doen?">
                <ul className="space-y-2.5">
                  {vacancy.responsibilities.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-text-secondary">
                      <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-orange" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </Block>

              <Block title="Wat vragen wij?">
                <ul className="space-y-2.5">
                  {vacancy.requirements.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-text-secondary">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-orange" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </Block>

              <Block title="Wat bieden wij?">
                <ul className="space-y-2.5">
                  {vacancy.offer.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-text-secondary">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-grass" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </Block>

              <Block title="Over het team">
                <p className="text-sm leading-relaxed text-text-secondary">{vacancy.aboutTeam}</p>
              </Block>
            </div>

            {/* Application block */}
            <aside className="space-y-6 lg:col-span-1">
              <div className="rounded-2xl border border-border bg-gradient-to-br from-card to-muted/40 p-6 shadow-sm">
                <div className="mb-4 flex items-center gap-2 text-orange">
                  <Users className="h-5 w-5" />
                  <span className="text-xs font-semibold uppercase tracking-wider">
                    Sollicitatieprocedure
                  </span>
                </div>
                <ol className="space-y-3 text-sm text-text-secondary">
                  <Step n={1} title="Solliciteren">
                    Vul het formulier hieronder in of mail naar werkenbij@elvion-group.nl.
                  </Step>
                  <Step n={2} title="Kennismaking">
                    Binnen 2 werkdagen nemen we contact op voor een korte telefonische kennismaking.
                  </Step>
                  <Step n={3} title="Gesprek op kantoor">
                    Pas je bij ons? Dan plannen we een gesprek met je toekomstige collega's in Breda.
                  </Step>
                  <Step n={4} title="Aanbod">
                    Bij een match ontvang je binnen één week een concreet aanbod.
                  </Step>
                </ol>
              </div>

              <div
                id="solliciteer"
                className="scroll-mt-24 rounded-2xl border border-border bg-card p-6 shadow-sm"
              >
                <div className="mb-4 flex items-center gap-2 text-orange">
                  <Send className="h-5 w-5" />
                  <span className="text-xs font-semibold uppercase tracking-wider">
                    Solliciteer
                  </span>
                </div>
                <h2 className="font-display text-xl font-extrabold text-foreground">
                  Solliciteer op {vacancy.title}
                </h2>
                <p className="mt-1 text-sm text-text-secondary">
                  Laat je gegevens achter en we reageren binnen 2 werkdagen.
                </p>
                <div className="mt-5">
                  <JobApplicationForm
                    vacancySlug={vacancy.slug}
                    vacancyTitle={vacancy.title}
                  />
                </div>
              </div>
            </aside>
          </div>
        </section>

        {/* Other vacancies */}
        {otherVacancies.length > 0 && (
          <section className="border-t border-border bg-muted/30">
            <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
              <h2 className="font-display text-2xl font-extrabold text-foreground">
                Andere vacatures
              </h2>
              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                {otherVacancies.map((v) => {
                  const VIcon = v.icon;
                  return (
                    <Link
                      key={v.slug}
                      to="/werken-bij/$slug"
                      params={{ slug: v.slug }}
                      className="group flex items-center gap-4 rounded-xl border border-border bg-card p-5 transition-shadow hover:shadow-md"
                    >
                      <div className="rounded-full bg-orange/10 p-2.5 text-orange">
                        <VIcon className="h-5 w-5" />
                      </div>
                      <div className="flex-1">
                        <p className="text-xs font-medium text-orange">{v.department}</p>
                        <h3 className="font-display text-base font-bold text-foreground group-hover:text-orange">
                          {v.title}
                        </h3>
                        <p className="text-xs text-text-secondary">
                          {v.location} · {v.type}
                        </p>
                      </div>
                      <ChevronRight className="h-5 w-5 text-text-secondary transition-transform group-hover:translate-x-0.5 group-hover:text-orange" />
                    </Link>
                  );
                })}
              </div>
              <div className="mt-6">
                <Link
                  to="/werken-bij"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-orange hover:underline"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Terug naar alle vacatures
                </Link>
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}

function Badge({
  icon: Icon,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
}) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-card px-3 py-1.5 text-xs font-medium text-foreground ring-1 ring-border">
      <Icon className="h-3.5 w-3.5 text-orange" />
      {children}
    </span>
  );
}

function Block({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="font-display text-2xl font-extrabold tracking-tight text-foreground">
        {title}
      </h2>
      <div className="mt-4">{children}</div>
    </div>
  );
}

function Step({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
  return (
    <li className="flex gap-3">
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-orange text-xs font-bold text-white">
        {n}
      </span>
      <div>
        <p className="text-sm font-semibold text-foreground">{title}</p>
        <p className="text-sm text-text-secondary">{children}</p>
      </div>
    </li>
  );
}
